import { Injectable, signal } from '@angular/core';
import { AccountModel } from '../api/models/account-model';
import { AccountService } from '../api/services/account.service';
import { AuthService } from './auth.service';
import { lastValueFrom } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { rxResource } from '@angular/core/rxjs-interop';

export type AccountSelectionRefreshResult =
  | {
      status: 'selected';
      account: AccountModel;
      previousAccountGuid: string;
      selectedAccountChanged: boolean;
    }
  | {
      status: 'no-active-accounts';
      previousAccountGuid: string;
    };

@Injectable({
  providedIn: 'root',
})
export class AccountStateService {
  readonly account = signal<AccountModel | undefined>(undefined);
  readonly availableAccounts = rxResource({
    params: () => ({}),
    stream: () =>
      this.accountService.accountsGet(
        this.authService.getAuthorisationHeaders(),
      ),
    defaultValue: [] as AccountModel[],
  });

  constructor(
    private accountService: AccountService,
    private authService: AuthService,
  ) {}

  getAccountDetails(accountGuid: string) {
    return this.accountService.accountDetailsGet(
      { accountGuid },
      this.authService.getAuthorisationHeaders(),
    );
  }

  async getAccount(refreshFromServer: boolean = false): Promise<AccountModel> {
    if (this.account() && !refreshFromServer) {
      return this.account()!;
    }

    const activeAccountGuid = this.authService.getActiveAccountGuid();

    if (!activeAccountGuid) {
      throw new Error('No active account selected');
    }

    const account = await lastValueFrom(
      this.accountService
        .accountDetailsGet(
          { accountGuid: activeAccountGuid },
          this.authService.getAuthorisationHeaders(),
        )
        .pipe(map((account) => account)),
    );

    this.account.set(account);
    return account;
  }

  switchAccount(newAccount: AccountModel) {
    return this.accountService
      .accountDetailsGet(
        { accountGuid: newAccount.guid },
        this.authService.getAuthorisationHeaders(),
      )
      .pipe(
        tap(() => {
          this.authService.setActiveAccountGuid(newAccount.guid);
          this.account.set(newAccount);
          this.availableAccounts.update((accounts) =>
            this.replaceAccount(accounts, newAccount),
          );
        }),
      );
  }

  async refreshAccountsAndReconcileSelected(
    preferredAccountGuid:
      string | null | undefined = this.authService.getActiveAccountGuid(),
  ): Promise<AccountSelectionRefreshResult> {
    const previousAccountGuid = this.authService.getActiveAccountGuid() ?? '';
    const accounts = await lastValueFrom(
      this.accountService.accountsGet(
        this.authService.getAuthorisationHeaders(),
      ),
    );

    this.availableAccounts.set(accounts);

    const selectedAccount =
      accounts.find((account) => account.guid === preferredAccountGuid) ??
      accounts[0];

    if (!selectedAccount) {
      this.clearSelectedAccount();
      return {
        status: 'no-active-accounts',
        previousAccountGuid,
      };
    }

    this.authService.setActiveAccountGuid(selectedAccount.guid);
    this.account.set(selectedAccount);

    return {
      status: 'selected',
      account: selectedAccount,
      previousAccountGuid,
      selectedAccountChanged: previousAccountGuid !== selectedAccount.guid,
    };
  }

  clearSelectedAccount(accountGuid?: string): void {
    if (!accountGuid || this.authService.activeAccountGuid() === accountGuid) {
      this.authService.setActiveAccountGuid('');
      this.account.set(undefined);
    }

    if (accountGuid) {
      this.availableAccounts.update((accounts) =>
        accounts.filter((account) => account.guid !== accountGuid),
      );
    }
  }

  private replaceAccount(accounts: AccountModel[], account: AccountModel) {
    const existingAccountIndex = accounts.findIndex(
      (candidate) => candidate.guid === account.guid,
    );

    if (existingAccountIndex === -1) {
      return accounts;
    }

    return accounts.map((candidate, index) =>
      index === existingAccountIndex ? account : candidate,
    );
  }
}
